import {
  EventType,
  type IAddToCartEvent,
  type IPageViewEvent,
  type IProductViewEvent,
  type ISearchEvent,
  type TEvent,
} from "../types";
import { BaseAdapter } from "./base-adapter";

declare global {
  interface Window {
    fbq?: (
      type: string,
      eventName: string,
      params?: Record<string, any>,
    ) => void;
    __kwikpass?: {
      // pixeldList can be array, string, null
      firePixelEventViaKP: (
        type: string,
        name: string,
        payload: any,
        pixeldList: any,
      ) => void;
    };
  }
}

/**
 * Configuration for the Facebook Pixel adapter
 */
export interface KpPixelAdapterConfig {
  /**
   * Facebook Pixel ID
   */
  pixelId: string;
}

/**
 * Facebook Pixel tracking adapter
 * Assumes that the Facebook Pixel script is already loaded in the page
 */
export class KpPixelAdapter extends BaseAdapter {
  public readonly name = "kp-facebook-pixel";

  // Removed unnecessary constructor that only calls super

  /**
   * Initialize the Facebook Pixel
   */
  public async initialize(): Promise<void> {
    console.log("[KP-Pixel] Initializing adapter...");

    if (this.initialized) {
      console.log("[KP-Pixel] Already initialized, skipping");
      return;
    }

    // Skip initialization if running on server
    if (typeof window === "undefined") {
      console.log("[KP-Pixel] Skipping initialization on server");
      return;
    }

    // Check if fbq is available
    if (!window.fbq) {
      console.warn(
        "[KP-Pixel] Facebook Pixel fbq not found. Make sure the script is loaded.",
      );
      return;
    }
    this.initialized = true;
  }

  /**
   * Track an event with Facebook Pixel
   * @param event The event to track
   */
  public async trackEvent(event: TEvent): Promise<void> {
    if (!this.initialized || typeof window === "undefined" || !window.fbq) {
      console.warn(
        "[KP-Pixel] Cannot track event, adapter not initialized or fbq not available",
        {
          eventType: event.type,
          initialized: this.initialized,
          windowDefined: typeof window !== "undefined",
          fbqAvailable: !!window.fbq,
        },
      );
      return;
    }

    switch (event.type) {
      case EventType.PRODUCT_VIEW:
        this.trackProductView(event as IProductViewEvent);
        break;
      case EventType.ADD_TO_CART:
        this.trackAddToCart(event as IAddToCartEvent);
        break;
      case EventType.SEARCH:
        this.trackSearch(event as ISearchEvent);
        break;
      default:
        console.warn(`[KP-Pixel] Unhandled event type "${event.type}"`);
      // For other events, track as custom events
      // const params = this.eventToParams(event);
      // window.fbq("trackCustom", event.type, this.enhanceWithAffiliateParams(params));
      // break;
    }
  }

  /**
   * Track a product view event
   * @param event The product view event
   */
  private trackProductView(event: IProductViewEvent): void {
    try {
      const params = {
        content_type: "product_group",
        content_ids: [event.productId],
        content_name: event.productName,
        content_category: event.category,
        value: event.price,
        currency: event.currency || "INR",
      };

      const enhancedParams = this.enhanceWithAffiliateParams(params);

      window.__kwikpass?.firePixelEventViaKP(
        "track",
        "ViewContent",
        enhancedParams,
        null,
      );
    } catch (error) {
      console.error("[KP-Pixel] Error tracking ProductView event:", error);
    }
  }

  /**
   * Track an add to cart event
   * @param event The add to cart event
   */
  private trackAddToCart(event: IAddToCartEvent): void {
    console.log("[KP-Pixel] Executing AddToCart tracking", {
      productId: event.productId,
      productName: event.productName,
      price: event.price,
      currency: event.currency,
      quantity: event.quantity,
    });

    try {
      const params = {
        content_type: "product_group",
        content_ids: [event.productId],
        content_name: event.productName,
        value: event.price,
        currency: event.currency || "INR",
      };

      const enhancedParams = this.enhanceWithAffiliateParams(params);
      // Uncomment to enable KwikPass integration
      // console.log(`[KP-Pixel] Sending AddToCart event via KwikPass`, { enhancedParams });
      window.__kwikpass?.firePixelEventViaKP(
        "track",
        "AddToCart",
        enhancedParams,
        null,
      );
    } catch (error) {
      console.error("[KP-Pixel] Error tracking AddToCart event:", error);
    }
  }

  /**
   * Track a search event
   * @param event The search event
   */
  private trackSearch(event: ISearchEvent): void {
    try {
      const params = {
        search_string: event.searchTerm,
        content_category: "product",
        content_ids: event.content_ids || [],
      };

      const enhancedParams = this.enhanceWithAffiliateParams(params);
      // Uncomment to enable KwikPass integration
      window.__kwikpass?.firePixelEventViaKP(
        "track",
        "Search",
        enhancedParams,
        null,
      );
    } catch (error) {
      console.error("[KP-Pixel] Error tracking Search event:", error);
    }
  }
}
